import React, { useCallback, useMemo, useState } from 'react'

import { Skeleton } from 'antd'
import { DateRangePicker, Flex, Header, VStack } from 'govwell-ui'
import {
  DateRangePickerValue,
  DateRangeType,
} from 'govwell-ui/components/DateRangePicker/DateRangePicker'
import { getDateRangePickerValue } from 'govwell-ui/components/DateRangePicker/util'
import {
  DateRange,
  OrderMode,
  ViewType,
  WidgetAggregationFn,
  WidgetDataGroupSortBy,
} from 'types/graphql'

import AIPageContainer from 'src/components/AIAssistant/AIPageContainer'
import BarChart from 'src/components/Visualizations/BarChart/BarChart'
import CirclePackingChart from 'src/components/Visualizations/CirclePackingChart/CirclePackingChart'
import {
  extractViewDataElementNumber,
  getBucketStartLabel,
} from 'src/components/Visualizations/util'
import { TailwindColor } from 'src/constants/theme/tailwind'
import { useAICAValueMetricsQuery, useAIROIQuery } from 'src/fetch/aiChat'
import { useGetMostUsedAIChatTopicsQuery } from 'src/fetch/aiChatTopics'
import { useGetDataForWidgetQuery } from 'src/fetch/widgets'
import { usePersistentIds } from 'src/hooks/use-persistent-ids'
import { useMyOrg, useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { filterNil } from 'src/utils'
import { DateFormats } from 'src/utils/date'
import { FeatureGate, useFeatureFlagging } from 'src/utils/featureFlagging/featureFlagging'
import { monitoring, MonitoringContextKey, MonitoringEvent } from 'src/utils/monitoring'
import { formatNumber, NumberFormat } from 'src/utils/number'

import AfterHoursCard from './components/AfterHoursCard'
import CategoriesSection from './components/CategoriesSection'
import ChartLegend from './components/ChartLegend'
import SummaryTile, { SummaryTileType } from './components/SummaryTile'
import TopicFrequencyTable from './TopicFrequencyTable'

const dateKey = 'Date'
const substantiveAnswerYesKey = 'Answered automatically'
const substantiveAnswerNoKey = 'Not answered automatically'
const keys = [substantiveAnswerNoKey, substantiveAnswerYesKey] // bottom-to-top

// Bar chart colors: blue for "Not answered", green for "Answered automatically"
const BAR_CHART_COLORS = [TailwindColor.Blue400, TailwindColor.Emerald500]

// Legend items for the bar chart (order matches keys array)
const BAR_CHART_LEGEND_ITEMS = [
  { label: substantiveAnswerNoKey, color: TailwindColor.Blue400 },
  { label: substantiveAnswerYesKey, color: TailwindColor.Emerald500 },
]

// Minimum hours saved to show the time saved section
const MIN_HOURS_SAVED_TO_SHOW = 5

const CommunityAssistantInsightsPage = () => {
  const { sessionId } = usePersistentIds()
  const org = useMyOrg()
  const organizationId = useMyOrgId()

  // Default to Past 12 Weeks for weekly view
  const [dateRange, setDateRange] = useState<DateRangePickerValue>(
    getDateRangePickerValue({ type: DateRangeType.Past12Weeks, timeZone: org?.timeZone })
  )

  const selectedDateRange: DateRange = useMemo(() => {
    return {
      start: dateRange.startDate?.toISOString() ?? '',
      end: dateRange.endDate?.toISOString() ?? '',
    }
  }, [dateRange])

  // Fetch ROI data
  const { aiChatROIData, isLoading: isROILoading } = useAIROIQuery({
    organizationId,
    startDate: selectedDateRange.start,
    endDate: selectedDateRange.end,
  })

  // Fetch AICA Value Metrics for comprehensive data
  const { valueMetrics, isLoading: isValueMetricsLoading } = useAICAValueMetricsQuery({
    organizationId,
    startDate: selectedDateRange.start,
    endDate: selectedDateRange.end,
  })

  const responseWasSubstantiveQueryInput = useMemo(() => {
    const functions: WidgetAggregationFn[] = ['Count']
    const groupSortBy: WidgetDataGroupSortBy = 'Key'
    const groupSortMode: OrderMode = 'Asc'
    const viewType: ViewType = 'CommunityAssistantAIChats'
    return {
      functionInputColumnTemplate: null,
      functions,
      dateRange: selectedDateRange,
      dateRangeColumnTemplate: { columnTemplateGeneratedId: 'AssistantAIQuestionCreatedAt' },
      filters: [],
      groupByColumnTemplates: [
        { columnTemplateGeneratedId: 'CommunityAssistantAIResponseWasSubstantive' },
      ],
      groupSortBy,
      groupSortMode,
      viewType,
      isTimeSeries: true,
    }
  }, [selectedDateRange])

  const {
    buckets: timeDataBuckets,
    bucketSize: timeBucketSize,
    isLoading: isTimeSeriesLoading,
  } = useGetDataForWidgetQuery(responseWasSubstantiveQueryInput)

  // Fetch most used topics
  const {
    results: topicsResults,
    isLoading: isTopicsLoading,
    error: topicsError,
    percentCovered,
  } = useGetMostUsedAIChatTopicsQuery({
    organizationId: org?.id ?? 0,
    startDate: selectedDateRange.start,
    endDate: selectedDateRange.end,
  })

  const timeSeriesData = useMemo(() => {
    if (!timeDataBuckets || !timeBucketSize) {
      return []
    }

    const allData = filterNil(
      timeDataBuckets.map((bucket) => {
        if (bucket.groups?.length === 0) {
          return null
        }

        const row: Record<string, number | string> = {
          [dateKey]: getBucketStartLabel(timeBucketSize, bucket.start, org?.timeZone),
        }

        bucket.groups?.forEach((group) => {
          const value = extractViewDataElementNumber(group.values?.[0]?.value) ?? 0
          const key = group.groupBys?.[0]?.key
          if (key?.__typename === 'ViewDataElementBoolean') {
            const name = key?.bool ? substantiveAnswerYesKey : substantiveAnswerNoKey
            row[name] = value
          }
        })

        return row
      })
    )

    // Skip leading empty weeks (weeks with no usage data)
    const firstNonEmptyIndex = allData.findIndex((row) => {
      const answered = (row[substantiveAnswerYesKey] as number) ?? 0
      const notAnswered = (row[substantiveAnswerNoKey] as number) ?? 0
      return answered > 0 || notAnswered > 0
    })

    return firstNonEmptyIndex > 0 ? allData.slice(firstNonEmptyIndex) : allData
  }, [timeDataBuckets, timeBucketSize, org?.timeZone])

  // Extract ROI data values
  const {
    numberOfAIChats,
    percentOfAIChatsWithSubstantiveAnswer,
    percentOfAIChatsWithThumbsUp,
    afterHoursQuestions,
    percentAfterHours,
    totalSessions,
  } = useMemo(() => {
    return {
      numberOfAIChats: aiChatROIData?.numberOfAIChats ?? 0,
      percentOfAIChatsWithSubstantiveAnswer:
        aiChatROIData?.percentOfAIChatsWithSubstantiveAnswer ?? 0,
      percentOfAIChatsWithThumbsUp: aiChatROIData?.percentOfAIChatsWithThumbsUp ?? 0,
      afterHoursQuestions: aiChatROIData?.afterHoursQuestions ?? 0,
      percentAfterHours: aiChatROIData?.percentAfterHours ?? 0,
      totalSessions: aiChatROIData?.totalSessions ?? 0,
    }
  }, [aiChatROIData])

  // Extract time saved from value metrics
  const totalEstimatedHoursSaved = useMemo(
    () => valueMetrics?.timeDeflection.totalEstimatedHoursSaved ?? 0,
    [valueMetrics]
  )

  // Get top departments from value metrics
  const topDepartments = useMemo(
    () => valueMetrics?.departmental.topDepartments ?? [],
    [valueMetrics]
  )

  // Extract complexity metrics
  const complexPercentage = useMemo(
    () => valueMetrics?.complexity.complexPercentage ?? 0,
    [valueMetrics]
  )

  // Extract cross-department metrics
  const crossDepartmentQuestions = useMemo(
    () => valueMetrics?.departmental.crossDepartmentQuestions ?? 0,
    [valueMetrics]
  )

  // Conditional display logic
  const shouldShowTimeSaved = totalEstimatedHoursSaved >= MIN_HOURS_SAVED_TO_SHOW
  const shouldShowAfterHours = percentAfterHours > 0
  const shouldShowCategories = topDepartments.length > 0

  // Build circle packing nodes from topics results
  const circlePackingNodes = useMemo(
    () =>
      topicsResults.map(({ aiChatTopic, count }) => ({
        id: aiChatTopic.id.toString(),
        label: aiChatTopic.topicName,
        value: count,
      })),
    [topicsResults]
  )

  // Handle topic click for monitoring
  const handleTopicClick = useCallback(
    (topicId: string) => {
      if (!sessionId) {
        return
      }
      monitoring.captureEvent(MonitoringEvent.AiHotTopicClicked, {
        [MonitoringContextKey.AiChatSessionId]: sessionId,
        [MonitoringContextKey.AiTopicId]: topicId,
      })
    },
    [sessionId]
  )

  // Handle topic hover (no-op for now, can be used for future styling)
  const handleTopicHover = useCallback((_topicId: string | null) => {
    // No-op for now, can be used for future styling/highlighting
  }, [])

  const { checkFeatureFlag } = useFeatureFlagging()
  const isAIChatTopicsEnabled = checkFeatureFlag(FeatureGate.AiChatTopics)

  const isLoading = isROILoading || isValueMetricsLoading

  return (
    <AIPageContainer
      title="AI Community Assistant"
      subtitle="Performance, usage metrics, and conversation insights"
    >
      <VStack gap={4} className="-mt-7">
        {/* Sticky Date Range Picker */}
        <Flex
          direction="row"
          justify="end"
          align="center"
          className="sticky top-0 z-100 bg-white py-2"
        >
          <DateRangePicker
            dateFormat={DateFormats.MonthNameDateNoTime}
            value={dateRange}
            onChange={setDateRange}
            timeZone={org?.timeZone}
          />
        </Flex>
        {/* Summary Section */}
        <VStack>
          <Header title="Summary" className="-mt-4 mb-4" />
          {isLoading ? (
            <Skeleton active />
          ) : (
            <Flex direction="row" gap={4} wrap="wrap">
              <SummaryTile
                type={SummaryTileType.Number}
                value={numberOfAIChats}
                label="Questions Answered"
                icon="FaMessage"
                iconBgColor="bg-blue-50"
                iconColor="text-blue-600"
                tooltip="The total number of questions received by the Community Assistant."
              />
              {!!percentOfAIChatsWithSubstantiveAnswer && (
                <SummaryTile
                  type={SummaryTileType.Percent}
                  value={percentOfAIChatsWithSubstantiveAnswer}
                  label="Answered Automatically"
                  icon="FaCheck"
                  iconBgColor="bg-emerald-50"
                  iconColor="text-emerald-600"
                  tooltip="The percent of questions that the Community Assistant answered automatically without needing to fall back to a contact."
                />
              )}
              <SummaryTile
                type={SummaryTileType.Percent}
                value={percentOfAIChatsWithThumbsUp}
                label="Satisfaction Rate"
                icon="FaThumbsUp"
                iconBgColor="bg-green-50"
                iconColor="text-green-600"
                tooltip="Percentage of users who gave a thumbs up, out of those who provided feedback."
              />
            </Flex>
          )}
        </VStack>

        {/* Secondary Stats Row: Staff Time Saved (1/3), Total Sessions (1/3), After Hours (1/3) */}
        {(shouldShowTimeSaved || shouldShowAfterHours) && !isLoading && (
          <div className="flex w-full items-stretch gap-4">
            {shouldShowTimeSaved && (
              <div className="w-1/3">
                <SummaryTile
                  type={SummaryTileType.Hours}
                  value={totalEstimatedHoursSaved}
                  label="Staff Time Saved"
                  icon="FaClock"
                  iconBgColor="bg-purple-50"
                  iconColor="text-purple-600"
                  tooltip="Estimated staff hours saved based on question complexity. Simple questions save ~5 minutes, complex questions save ~15 minutes each."
                />
              </div>
            )}
            <div className="w-1/3">
              <SummaryTile
                type={SummaryTileType.Number}
                value={totalSessions}
                label="Total Sessions"
                icon="FaUsers"
                iconBgColor="bg-orange-50"
                iconColor="text-orange-600"
                tooltip="The total number of unique chat sessions initiated with the Community Assistant."
              />
            </div>
            {shouldShowAfterHours && (
              <div className="w-1/3">
                <AfterHoursCard
                  percentAfterHours={percentAfterHours}
                  afterHoursQuestions={afterHoursQuestions}
                  compact
                  tooltip="The percentage of questions asked outside of standard business hours (9 AM - 5 PM, Monday - Friday)."
                />
              </div>
            )}
          </div>
        )}

        {/* Usage Section */}
        <VStack gap={4}>
          <div className="flex w-full items-center justify-between">
            <Header title="Questions Over Time" />
            <ChartLegend items={BAR_CHART_LEGEND_ITEMS} />
          </div>
          <Flex gap={4} wrap="wrap">
            <BarChart
              data={timeSeriesData}
              indexBy={dateKey}
              keys={keys}
              colors={BAR_CHART_COLORS}
              bottomAxisLabel="Week"
              leftAxisLabel="Questions"
              labelTooltip="Total questions asked per week, split by whether they were answered automatically"
              isLoading={isTimeSeriesLoading}
              isTimeSeries
            />
          </Flex>
        </VStack>

        {/* Conversation Complexity Section */}
        {!isLoading && (complexPercentage >= 20 || crossDepartmentQuestions > 2) && (
          <VStack gap={4}>
            <Header title="Conversation Complexity" />
            <Flex direction="row" gap={4} wrap="wrap">
              {complexPercentage >= 20 && (
                <SummaryTile
                  type={SummaryTileType.Percent}
                  value={complexPercentage}
                  label="Complex Questions"
                  icon="FaBrain"
                  iconBgColor="bg-violet-50"
                  iconColor="text-violet-600"
                  tooltip="Percentage of questions classified as complex (multi-step, cross-departmental, or requiring detailed explanations)"
                />
              )}
              {crossDepartmentQuestions > 2 && (
                <SummaryTile
                  type={SummaryTileType.Number}
                  value={crossDepartmentQuestions}
                  label="Cross-Dept Conversations"
                  icon="FaLayerGroup"
                  iconBgColor="bg-amber-50"
                  iconColor="text-amber-600"
                  tooltip="Conversations that spanned multiple departments or topic areas"
                />
              )}
            </Flex>
          </VStack>
        )}

        {/* Categories Section */}
        {shouldShowCategories && (
          <CategoriesSection topDepartments={topDepartments} isLoading={isValueMetricsLoading} />
        )}

        {/* Topics Section (existing) */}
        {isAIChatTopicsEnabled && (
          <VStack gap={4}>
            <Header title="Top Topics" subtitle="Most frequently discussed topics" />
            <CirclePackingChart
              nodes={circlePackingNodes}
              isLoading={isTopicsLoading}
              error={topicsError ?? null}
              emptyMessage="No topics recorded for this range."
              subLabel={
                percentCovered
                  ? `${formatNumber(percentCovered, { format: NumberFormat.Percentage })} of conversations within this time range are covered by the most used topics.`
                  : undefined
              }
              label="Top Topics in Date Range"
              labelTooltip="The most frequently discussed topics by Community Assistant users for the selected filters"
              onClick={handleTopicClick}
              onHover={handleTopicHover}
            />
            <TopicFrequencyTable topics={circlePackingNodes} isLoading={isTopicsLoading} />
          </VStack>
        )}
      </VStack>
    </AIPageContainer>
  )
}

export default React.memo(CommunityAssistantInsightsPage)
